import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/system";
import axios from "axios";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import ChatDetailsModal from "../../components/Modals/ChatDetailsModal";

/** 
 * LIGHT THEME STYLES 
 * (Previously 'Dark theme with neon glow')
 * Using a light background (#ecf7fe), dark text (#333), and subtle borders.
 */

// Main Container
const DashboardContainer = styled(Box)({
    backgroundColor: "#ecf7fe", // Very light blue
    color: "#333",
    minHeight: "100vh",
    padding: "20px",
    boxSizing: "border-box",
});

// StatBox (light style)
const StatBox = ({ title, value }) => (
    <Card
        sx={{
            backgroundColor: "#fff",
            color: "#1976d2",
            border: "1px solid #ddd",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            padding: "16px",
            textAlign: "center",
        }}
    >
        <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const AiResponseDashboard = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // Fetch data (only logs for sail-wingman bot)
    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    "https://api.swancreate.com/api/llm/AI_PromptResponseLog",
                    {
                        params: {
                            BotID: "sail-wingman", // Fetch only logs related to sail-wingman
                        },
                    }
                );
                setLogs(response.data);
            } catch (error) {
                console.error("Error fetching logs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    // Open modal with selected row data
    const handleOpenModal = (row) => {
        setSelectedRow(row);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedRow(null);
    };

    // Map API response to DataGrid rows
    const rows = logs.map((item, index) => ({
        id: index,
        BotID: item.BotID,
        SchoolDistrict: item.SchoolDistrict || "N/A",
        PromptText: item.PromptText,
        ResponseText: item.ResponseText,
        TokenAmount: item.TokenAmount,
        PromptDate: item.PromptDate,
        ResponseDate: item.ResponseDate,
        SourcePageURL: item.SourcePageURL || "Unknown",
    }));

    // Statistics
    const totalMessages = rows.length;
    const uniqueBots = new Set(rows.map((log) => log.BotID)).size;
    const totalTokens = rows.reduce((sum, log) => sum + (log.TokenAmount || 0), 0);

    // Build Line Chart Data using BotID as the series key
    const dailyCountsByBot = {};
    rows.forEach((log) => {
        const dateStr = log.PromptDate
            ? new Date(log.PromptDate).toISOString().slice(0, 10)
            : "Unknown";
        const bot = log.BotID || "Unknown";
        if (!dailyCountsByBot[dateStr]) {
            dailyCountsByBot[dateStr] = {};
        }
        dailyCountsByBot[dateStr][bot] =
            (dailyCountsByBot[dateStr][bot] || 0) + 1;
    });

    // Determine date range based on current date
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    let startDate, endDate;
    if (currentDate < new Date(currentYear, 6, 1)) {
        // Before July 1 => first half of the year: Jan 1 to June 30
        startDate = new Date(currentYear, 0, 1);
        endDate = new Date(currentYear, 5, 30);
    } else {
        // On or after July 1 => second half: July 1 to Dec 31
        startDate = new Date(currentYear, 6, 1);
        endDate = new Date(currentYear, 11, 31);
    }

    const datesArray = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        datesArray.push(new Date(d).toISOString().slice(0, 10));
    }

    const chartData = datesArray.map((date) => {
        const dataForDay = dailyCountsByBot[date] || {};
        // All bots from rows
        const bots = Array.from(new Set(rows.map((row) => row.BotID || "Unknown")));
        const filledData = {};
        bots.forEach((bot) => {
            filledData[bot] = dataForDay[bot] || 0;
        });
        return { date, ...filledData };
    });

    // Assign colors to each bot (simple color palette)
    const botColorPalette = [
        "#1976d2",
        "#388e3c",
        "#f57c00",
        "#d32f2f",
        "#9c27b0",
        "#455a64",
        "#e91e63",
    ];
    const botColorMapping = {};
    const bots = Array.from(new Set(rows.map((row) => row.BotID || "Unknown")));
    bots.forEach((bot, index) => {
        botColorMapping[bot] = botColorPalette[index % botColorPalette.length];
    });

    // Build Pie Chart Data: count messages per Bot
    const pieDataObj = {};
    rows.forEach((row) => {
        const bot = row.BotID || "Unknown";
        pieDataObj[bot] = (pieDataObj[bot] || 0) + 1;
    });
    const pieDataArray = Object.keys(pieDataObj).map((key) => ({
        name: key,
        value: pieDataObj[key],
    }));

    // DataGrid Columns
    const columns = [
        { field: "BotID", headerName: "Bot", flex: 0.8, sortable: true },
        {
            field: "SchoolDistrict",
            headerName: "School District",
            flex: 1,
            sortable: true,
        },
        { field: "PromptText", headerName: "Prompt", flex: 1.5 },
        { field: "ResponseText", headerName: "Response", flex: 2 },
        { field: "TokenAmount", headerName: "Tokens", flex: 0.5, sortable: true },
        {
            field: "PromptDate",
            headerName: "Prompt Date",
            flex: 0.8,
            valueFormatter: (params) =>
                params.value ? new Date(params.value).toLocaleString() : "",
        },
        {
            field: "ResponseDate",
            headerName: "Response Date",
            flex: 0.8,
            valueFormatter: (params) =>
                params.value ? new Date(params.value).toLocaleString() : "",
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.8,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#1565c0" },
                    }}
                    onClick={() => handleOpenModal(params.row)}
                >
                    View Details
                </Button>
            ),
        },
    ];

    return (
        <DashboardContainer>
            {loading ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="80vh"
                >
                    <CircularProgress sx={{ color: "#1976d2" }} />
                </Box>
            ) : (
                <>
                    {/* Statistics */}
                    <Grid container spacing={3} sx={{ marginBottom: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <StatBox title="Total Messages" value={totalMessages} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatBox title="Unique Bots" value={uniqueBots} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatBox title="Total Tokens" value={totalTokens} />
                        </Grid>
                    </Grid>

                    {/* Line Chart: Message Volume Over Time by Bot */}
                    <Box
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            padding: 2,
                            marginBottom: 4,
                            border: "1px solid #ddd",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Typography variant="h6" sx={{ marginBottom: 2, color: "#333" }}>
                            Message Volume Over Time (by Bot)
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={chartData}>
                                <CartesianGrid stroke="#ccc" />
                                <XAxis dataKey="date" stroke="#333" />
                                <YAxis stroke="#333" />
                                <Tooltip />
                                <Legend />
                                {bots.map((bot) => (
                                    <Line
                                        key={bot}
                                        type="monotone"
                                        dataKey={bot}
                                        stroke={botColorMapping[bot]}
                                        strokeWidth={2}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Pie Chart: Distribution of Messages by Bot */}
                    <Box
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            padding: 2,
                            marginBottom: 4,
                            border: "1px solid #ddd",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ marginBottom: 2, textAlign: "center", color: "#333" }}
                        >
                            Chat Volume by Bot
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                                <Pie
                                    data={pieDataArray}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={130}
                                    fill="#1976d2"
                                    label
                                >
                                    {pieDataArray.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={botColorMapping[entry.name] || "#1976d2"}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Data Grid */}
                    <Box
                        sx={{
                            height: 500,
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            border: "1px solid #ddd",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        }}
                    >
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            autoPageSize
                            disableSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-cell": {
                                    color: "#333",
                                    borderBottom: "1px solid #eee",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "#f5f5f5",
                                    color: "#333",
                                    borderBottom: "1px solid #ddd",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#f9f9f9",
                                },
                            }}
                        />
                    </Box>

                    {/* Chat Details Modal */}
                    <ChatDetailsModal
                        open={modalOpen}
                        onClose={handleCloseModal}
                        rowData={selectedRow}
                    />
                </>
            )}
        </DashboardContainer>
    );
};

export default AiResponseDashboard;
